export const fmtNum = (n: number) =>
	new Intl.NumberFormat("en-US").format(Math.round(n));

const moneyFormatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

/** Money metrics on campaign card (Budget, Cost, Profit, Dep $, etc.) — always 2 decimals. */
export const fmtMoney = (n: number) => moneyFormatter.format(n);

export const fmtDec = (n: number, d = 2) => n.toFixed(d);

/**
 * ROI ratio → percent string for campaign card tiles.
 * Backend sends ratio (0.2); UI shows "20.00%" (no space before %).
 */
export const fmtRoiPct = (ratio: number) => `${(ratio * 100).toFixed(2)}%`;

/** Green when value > 0, red when value <= 0 (Profit, ROI on campaign card). */
export const signedMetricModifier = (value: number): string =>
	value > 0 ? "campaign-detail__roas--good" : "campaign-detail__roas--low";

export const roasModifier = signedMetricModifier;

/**
 * High-contrast palette — each GADS / mock placement resolves to a distinct hue.
 * Keep hex values unique within GADS_API_PLACEMENTS (see test).
 */
export const PLACEMENT_COLORS: Record<string, string> = {
	Search: "#2563eb",
	Display: "#6366f1",
	YouTube: "#e11d48",
	Others: "#475569",
	CONFIGURED: "#94a3b8",
	MIXED: "#78716c",
	CONTENT: "#0891b2",
	DISPLAY: "#6366f1",
	SEARCH: "#2563eb",
	SEARCH_PARTNERS: "#f59e0b",
	SEARCH_TOP: "#7c3aed",
	SEARCH_OTHER: "#16a34a",
	SEARCH_PARTNER_OTHER: "#ea580c",
	SEARCH_PARTNER_TOP: "#eab308",
	YOUTUBE: "#e11d48",
	YOUTUBE_WATCH: "#f43f5e",
	YOUTUBE_SEARCH: "#ec4899",
	YOUTUBE_IN_STREAM: "#d946ef",
};

/** Placement names from GADS API / campaign card (geo + ad group). */
export const GADS_API_PLACEMENTS = [
	"SEARCH",
	"SEARCH_PARTNERS",
	"CONTENT",
	"CONFIGURED",
	"MIXED",
	"SEARCH_TOP",
	"SEARCH_OTHER",
	"SEARCH_PARTNER_OTHER",
	"SEARCH_PARTNER_TOP",
	"YOUTUBE",
	"YOUTUBE_WATCH",
	"YOUTUBE_SEARCH",
	"YOUTUBE_IN_STREAM",
] as const;

/** Mock / Lovable Title Case placement names. */
export const MOCK_PLACEMENTS = ["Search", "Display", "YouTube", "Others"] as const;

const PLACEMENT_FALLBACK_COLOR = PLACEMENT_COLORS.Others;

/** GADS / buyer-journal placement tokens → palette bucket (mock uses Title Case keys). */
function placementBucket(name: string): keyof typeof PLACEMENT_COLORS {
	if (PLACEMENT_COLORS[name as keyof typeof PLACEMENT_COLORS]) {
		return name as keyof typeof PLACEMENT_COLORS;
	}

	const upper = name.trim().toUpperCase();
	if (upper.startsWith("SEARCH_PARTNER")) return "SEARCH_PARTNER_OTHER";
	if (upper === "SEARCH_PARTNERS") return "SEARCH_PARTNERS";
	if (upper.startsWith("SEARCH")) return "SEARCH";
	if (upper.includes("YOUTUBE")) return "YouTube";
	if (upper === "CONTENT") return "CONTENT";
	if (upper.includes("DISPLAY")) return "Display";
	if (upper === "OTHERS") return "Others";

	return "Others";
}

/** Resolve chart/UI color for a placement name from API or mocks. */
export function placementColor(name: string): string {
	const trimmed = name.trim();
	const upper = trimmed.toUpperCase();
	if (PLACEMENT_COLORS[upper]) return PLACEMENT_COLORS[upper];
	if (PLACEMENT_COLORS[trimmed]) return PLACEMENT_COLORS[trimmed];
	return PLACEMENT_COLORS[placementBucket(name)] ?? PLACEMENT_FALLBACK_COLOR;
}
